/**
 * Notifications Screen
 * Displays all user notifications with nice UI and lottie support
 */

import { useState, useCallback, useRef, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Pressable,
  RefreshControl,
  ActivityIndicator,
  Animated,
} from "react-native";
import { Swipeable, GestureHandlerRootView } from "react-native-gesture-handler";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { formatDistanceToNow } from "date-fns";
import { nl } from "date-fns/locale";
import LottieView from "lottie-react-native";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useNotifications, InAppNotification } from "@/lib/notifications/useNotifications";
import { getLottieSource } from "@/lib/lottie-registry";
import { clearDeviceNotifications } from "@/lib/notifications";

/**
 * Get icon for notification type
 */
const getNotificationIcon = (type: string): keyof typeof Ionicons.glyphMap => {
  switch (type) {
    case "challenge_available":
      return "flash";
    case "challenge_approved":
      return "trophy";
    case "challenge_rejected":
      return "close-circle";
    case "challenge_submission":
    case "challenge_submission_updated":
      return "camera";
    case "announcement":
      return "megaphone";
    case "score_update":
      return "star";
    case "team_update":
      return "people";
    case "event_reminder":
      return "calendar";
    case "chat_message":
      return "chatbubble";
    default:
      return "notifications";
  }
};

/**
 * Get color for notification type
 */
const getNotificationColor = (type: string): string => {
  switch (type) {
    case "challenge_available":
      return "#10B981"; // green - new challenge!
    case "challenge_approved":
      return "#10B981"; // green
    case "challenge_rejected":
      return "#EF4444"; // red
    case "challenge_submission":
    case "challenge_submission_updated":
      return "#F59E0B"; // orange/yellow for admin attention
    case "announcement":
      return "#8B5CF6"; // purple
    case "score_update":
      return "#F59E0B"; // yellow
    case "team_update":
      return "#3B82F6"; // blue
    case "event_reminder":
      return "#EC4899"; // pink
    case "chat_message":
      return "#6366F1"; // indigo
    default:
      return "#6B7280"; // gray
  }
};

/**
 * Swipeable Notification Item Component
 */
interface NotificationItemProps {
  notification: InAppNotification;
  onPress: () => void;
  onMarkAsRead: () => void;
  colors: ReturnType<typeof useThemeColors>;
}

const NotificationItem = ({ notification, onPress, onMarkAsRead, colors }: NotificationItemProps) => {
  const icon = getNotificationIcon(notification.type);
  const iconColor = getNotificationColor(notification.type);
  const swipeableRef = useRef<Swipeable>(null);

  /**
   * Render swipe action (mark as read)
   */
  const renderRightActions = (
    progress: Animated.AnimatedInterpolation<number>,
    dragX: Animated.AnimatedInterpolation<number>
  ) => {
    const translateX = dragX.interpolate({
      inputRange: [-100, 0],
      outputRange: [0, 100],
      extrapolate: "clamp",
    });

    return (
      <Animated.View
        style={{
          backgroundColor: "#3B82F6",
          justifyContent: "center",
          alignItems: "center",
          width: 100,
          transform: [{ translateX }],
        }}
      >
        <Ionicons name="checkmark-circle" size={28} color="#FFFFFF" />
        <Text style={{ color: "#FFFFFF", fontSize: 12, marginTop: 4 }}>
          Gelezen
        </Text>
      </Animated.View>
    );
  };

  const renderLeftActions = (
    progress: Animated.AnimatedInterpolation<number>,
    dragX: Animated.AnimatedInterpolation<number>
  ) => {
    const translateX = dragX.interpolate({
      inputRange: [0, 100],
      outputRange: [-100, 0],
      extrapolate: "clamp",
    });

    return (
      <Animated.View
        style={{
          backgroundColor: "#3B82F6",
          justifyContent: "center",
          alignItems: "center",
          width: 100,
          transform: [{ translateX }],
        }}
      >
        <Ionicons name="checkmark-circle" size={28} color="#FFFFFF" />
        <Text style={{ color: "#FFFFFF", fontSize: 12, marginTop: 4 }}>
          Gelezen
        </Text>
      </Animated.View>
    );
  };

  /**
   * Handle swipe open
   */
  const handleSwipeOpen = () => {
    if (!notification.is_read) {
      onMarkAsRead();
    }
    swipeableRef.current?.close();
  };

  // If already read, don't show swipe actions
  if (notification.is_read) {
    return (
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.7}
        style={{ backgroundColor: colors.surface }}
        className="px-4 py-4 flex-row items-start"
      >
        <NotificationContent
          notification={notification}
          icon={icon}
          iconColor={iconColor}
          colors={colors}
        />
      </TouchableOpacity>
    );
  }

  return (
    <Swipeable
      ref={swipeableRef}
      renderRightActions={renderRightActions}
      renderLeftActions={renderLeftActions}
      onSwipeableOpen={handleSwipeOpen}
      friction={2}
      overshootRight={false}
      overshootLeft={false}
    >
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.7}
        style={{
          backgroundColor: "#EFF6FF", // Light blue background for unread
          borderLeftWidth: 4,
          borderLeftColor: "#3B82F6", // Blue border
        }}
        className="px-4 py-4 flex-row items-start"
      >
        <NotificationContent
          notification={notification}
          icon={icon}
          iconColor={iconColor}
          colors={colors}
        />
      </TouchableOpacity>
    </Swipeable>
  );
};

/**
 * Notification Content (shared between read/unread states)
 */
interface NotificationContentProps {
  notification: InAppNotification;
  icon: keyof typeof Ionicons.glyphMap;
  iconColor: string;
  colors: ReturnType<typeof useThemeColors>;
}

// Use central lottie registry - no local map needed

const NotificationContent = ({
  notification,
  icon,
  iconColor,
  colors,
}: NotificationContentProps) => {
  // Get Lottie source from the notification's animation field using central registry
  const lottieSource = notification.lottie_animation 
    ? getLottieSource(notification.lottie_animation) 
    : null;

  // Extract challenge data if available
  const actionData = notification.action_data as any;
  const isChallengeNotification = notification.type === "challenge_available";
  const challengePoints = actionData?.points;
  const challengeDeadline = actionData?.deadline;

  return (
    <>
      {/* Icon with optional lottie animation */}
      <View className="relative">
        <View
          style={{ backgroundColor: iconColor + "20" }}
          className="w-12 h-12 rounded-full items-center justify-center overflow-hidden"
        >
          {lottieSource ? (
            <LottieView
              source={lottieSource}
              autoPlay
              loop
              style={{ width: 40, height: 40 }}
            />
          ) : (
            <Ionicons name={icon} size={24} color={iconColor} />
          )}
        </View>
        {/* Unread indicator - blue dot */}
        {!notification.is_read && (
          <View
            className="absolute -top-1 -right-1 w-3 h-3 rounded-full"
            style={{ backgroundColor: "#3B82F6" }}
          />
        )}
      </View>

      {/* Content */}
      <View className="flex-1 ml-3">
        <Text
          style={{ color: colors.text }}
          className={`text-base ${notification.is_read ? "" : "font-semibold"}`}
          numberOfLines={1}
        >
          {notification.title}
        </Text>
        <Text
          style={{ color: colors.textSecondary }}
          className="text-sm mt-1"
          numberOfLines={2}
        >
          {notification.body}
        </Text>
        
        {/* Challenge-specific badges */}
        {isChallengeNotification && (challengePoints || challengeDeadline) && (
          <View className="flex-row items-center mt-2 flex-wrap">
            {challengePoints > 0 && (
              <View 
                className="flex-row items-center mr-3 px-2 py-0.5 rounded-full"
                style={{ backgroundColor: "#F59E0B20" }}
              >
                <Ionicons name="star" size={12} color="#F59E0B" />
                <Text className="text-xs ml-1 font-medium" style={{ color: "#F59E0B" }}>
                  {challengePoints} pts
                </Text>
              </View>
            )}
            {challengeDeadline && (
              <View 
                className="flex-row items-center px-2 py-0.5 rounded-full"
                style={{ backgroundColor: "#EF444420" }}
              >
                <Ionicons name="time" size={12} color="#EF4444" />
                <Text className="text-xs ml-1 font-medium" style={{ color: "#EF4444" }}>
                  {new Date(challengeDeadline).toLocaleDateString("nl-NL", {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Text>
              </View>
            )}
          </View>
        )}
        
        <Text
          style={{ color: colors.textTertiary }}
          className="text-xs mt-2"
        >
          {formatDistanceToNow(new Date(notification.created_at), {
            addSuffix: true,
            locale: nl,
          })}
        </Text>
      </View>

      {/* Arrow */}
      <View className="ml-2 self-center">
        <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
      </View>
    </>
  );
};

/**
 * Empty State Component
 */
const EmptyState = ({ colors }: { colors: ReturnType<typeof useThemeColors> }) => (
  <View className="flex-1 items-center justify-center py-20">
    <View
      className="w-20 h-20 rounded-full items-center justify-center mb-4"
      style={{ backgroundColor: colors.surfaceSecondary }}
    >
      <Ionicons name="notifications-off-outline" size={40} color={colors.textTertiary} />
    </View>
    <Text style={{ color: colors.text }} className="text-lg font-semibold">
      Geen meldingen
    </Text>
    <Text style={{ color: colors.textSecondary }} className="text-center mt-2 px-8">
      Je hebt nog geen meldingen ontvangen. Als er iets gebeurt, zie je het hier.
    </Text>
  </View>
);

/**
 * Notifications Screen
 */
export default function NotificationsScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const {
    notifications,
    unreadCount,
    isLoading,
    refetch,
    markAsRead,
    markAllAsRead,
    permissionStatus,
    showPermissionPrompt,
    handleEnableNotifications,
    handleDismissPrompt,
    handleOpenSettings: handleOpenOsSettings,
  } = useNotifications();
  const [refreshing, setRefreshing] = useState(false);

  // Mark all as read + clear device tray/badge when screen opens
  useEffect(() => {
    if (!isLoading && unreadCount > 0) {
      markAllAsRead();
    }
    clearDeviceNotifications();
  }, [isLoading]);

  /**
   * Handle pull-to-refresh
   */
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  /**
   * Handle notification press
   */
  const handleNotificationPress = async (notification: InAppNotification) => {
    // Mark as read
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Navigate if action_url is set
    if (notification.action_url) {
      router.push(notification.action_url as any);
    }
  };

  /**
   * Handle mark all as read
   */
  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  /**
   * Navigate to notification settings
   */
  const handleOpenSettings = () => {
    router.push("/(tabs)/profile/notifications");
  };

  // Group notifications by date
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const groupedNotifications = {
    today: notifications.filter((n) => new Date(n.created_at) >= today),
    yesterday: notifications.filter(
      (n) => new Date(n.created_at) >= yesterday && new Date(n.created_at) < today
    ),
    thisWeek: notifications.filter(
      (n) => new Date(n.created_at) >= weekAgo && new Date(n.created_at) < yesterday
    ),
    older: notifications.filter((n) => new Date(n.created_at) < weekAgo),
  };

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "left", "right"]}>
        {/* Header */}
        <View
          className="px-4 py-4 flex-row items-center justify-between"
          style={{
            backgroundColor: colors.surface,
            borderBottomWidth: 1,
            borderBottomColor: colors.border,
          }}
        >
        <View className="flex-row items-center">
          <TouchableOpacity
            onPress={() => router.back()}
            className="mr-3 p-1"
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name="arrow-back" size={24} color={colors.text} />
          </TouchableOpacity>
          <View>
            <Text style={{ color: colors.text }} className="text-xl font-bold">
              Meldingen
            </Text>
            {unreadCount > 0 && (
              <Text style={{ color: colors.textSecondary }} className="text-sm">
                {unreadCount} ongelezen
              </Text>
            )}
          </View>
        </View>

        <View className="flex-row items-center">
          {unreadCount > 0 && (
            <TouchableOpacity
              onPress={handleMarkAllAsRead}
              className="mr-3 px-3 py-2 rounded-lg"
              style={{ backgroundColor: colors.primaryLight }}
            >
              <Text style={{ color: colors.primary }} className="font-medium text-sm">
                Alles gelezen
              </Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity onPress={handleOpenSettings} className="p-2">
            <Ionicons name="settings-outline" size={22} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Soft Permission Prompt */}
      {showPermissionPrompt && permissionStatus === "denied" && (
        <View
          className="mx-4 mt-3 rounded-xl p-4 flex-row items-start"
          style={{ backgroundColor: "#FEE2E2" }}
        >
          <Ionicons name="notifications-off-outline" size={20} color="#EF4444" />
          <View className="flex-1 mx-3">
            <Text className="font-medium text-sm" style={{ color: "#991B1B" }}>
              Push meldingen staan uit
            </Text>
            <Text className="text-xs mt-0.5" style={{ color: "#B91C1C" }}>
              Open je telefoon instellingen om meldingen aan te zetten.
            </Text>
            <View className="flex-row mt-2">
              <Pressable
                onPress={handleOpenOsSettings}
                className="rounded-lg px-3 py-1.5 mr-2"
                style={{ backgroundColor: "#EF4444" }}
                accessibilityRole="button"
                accessibilityLabel="Open instellingen"
              >
                <Text className="text-xs font-medium text-white">
                  Open instellingen
                </Text>
              </Pressable>
              <Pressable
                onPress={handleDismissPrompt}
                className="rounded-lg px-3 py-1.5"
                style={{ backgroundColor: "#FCA5A520" }}
                accessibilityRole="button"
                accessibilityLabel="Later"
              >
                <Text className="text-xs font-medium" style={{ color: "#991B1B" }}>
                  Later
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      )}

      {showPermissionPrompt && permissionStatus === "undetermined" && (
        <View
          className="mx-4 mt-3 rounded-xl p-4 flex-row items-start"
          style={{ backgroundColor: "#EDE9FE" }}
        >
          <Ionicons name="notifications-outline" size={20} color="#7C3AED" />
          <View className="flex-1 mx-3">
            <Text className="font-medium text-sm" style={{ color: "#5B21B6" }}>
              Mis geen berichten!
            </Text>
            <Text className="text-xs mt-0.5" style={{ color: "#6D28D9" }}>
              Zet push meldingen aan zodat je altijd op de hoogte bent.
            </Text>
            <View className="flex-row mt-2">
              <Pressable
                onPress={handleEnableNotifications}
                className="rounded-lg px-3 py-1.5 mr-2"
                style={{ backgroundColor: "#7C3AED" }}
                accessibilityRole="button"
                accessibilityLabel="Meldingen aanzetten"
              >
                <Text className="text-xs font-medium text-white">
                  Aanzetten
                </Text>
              </Pressable>
              <Pressable
                onPress={handleDismissPrompt}
                className="rounded-lg px-3 py-1.5"
                style={{ backgroundColor: "#C4B5FD20" }}
                accessibilityRole="button"
                accessibilityLabel="Later"
              >
                <Text className="text-xs font-medium" style={{ color: "#5B21B6" }}>
                  Later
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      )}

      {/* Loading State */}
      {isLoading && notifications.length === 0 ? (
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={{ color: colors.textSecondary }} className="mt-2">
            Laden...
          </Text>
        </View>
      ) : notifications.length === 0 ? (
        <EmptyState colors={colors} />
      ) : (
        <ScrollView
          className="flex-1"
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={colors.primary}
            />
          }
        >
          {/* Today */}
          {groupedNotifications.today.length > 0 && (
            <View>
              <Text
                style={{ color: colors.textTertiary }}
                className="px-4 py-3 text-sm font-semibold uppercase tracking-wide"
              >
                Vandaag
              </Text>
              <View
                className="mx-4 rounded-xl overflow-hidden"
                style={{ borderWidth: 1, borderColor: colors.border }}
              >
                {groupedNotifications.today.map((notification, index) => (
                  <View key={notification.id}>
                    {index > 0 && (
                      <View style={{ height: 1, backgroundColor: colors.border }} />
                    )}
                    <NotificationItem
                      notification={notification}
                      onPress={() => handleNotificationPress(notification)}
                      onMarkAsRead={() => markAsRead(notification.id)}
                      colors={colors}
                    />
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* Yesterday */}
          {groupedNotifications.yesterday.length > 0 && (
            <View className="mt-4">
              <Text
                style={{ color: colors.textTertiary }}
                className="px-4 py-3 text-sm font-semibold uppercase tracking-wide"
              >
                Gisteren
              </Text>
              <View
                className="mx-4 rounded-xl overflow-hidden"
                style={{ borderWidth: 1, borderColor: colors.border }}
              >
                {groupedNotifications.yesterday.map((notification, index) => (
                  <View key={notification.id}>
                    {index > 0 && (
                      <View style={{ height: 1, backgroundColor: colors.border }} />
                    )}
                    <NotificationItem
                      notification={notification}
                      onPress={() => handleNotificationPress(notification)}
                      onMarkAsRead={() => markAsRead(notification.id)}
                      colors={colors}
                    />
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* This Week */}
          {groupedNotifications.thisWeek.length > 0 && (
            <View className="mt-4">
              <Text
                style={{ color: colors.textTertiary }}
                className="px-4 py-3 text-sm font-semibold uppercase tracking-wide"
              >
                Deze week
              </Text>
              <View
                className="mx-4 rounded-xl overflow-hidden"
                style={{ borderWidth: 1, borderColor: colors.border }}
              >
                {groupedNotifications.thisWeek.map((notification, index) => (
                  <View key={notification.id}>
                    {index > 0 && (
                      <View style={{ height: 1, backgroundColor: colors.border }} />
                    )}
                    <NotificationItem
                      notification={notification}
                      onPress={() => handleNotificationPress(notification)}
                      onMarkAsRead={() => markAsRead(notification.id)}
                      colors={colors}
                    />
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* Older */}
          {groupedNotifications.older.length > 0 && (
            <View className="mt-4">
              <Text
                style={{ color: colors.textTertiary }}
                className="px-4 py-3 text-sm font-semibold uppercase tracking-wide"
              >
                Ouder
              </Text>
              <View
                className="mx-4 rounded-xl overflow-hidden"
                style={{ borderWidth: 1, borderColor: colors.border }}
              >
                {groupedNotifications.older.map((notification, index) => (
                  <View key={notification.id}>
                    {index > 0 && (
                      <View style={{ height: 1, backgroundColor: colors.border }} />
                    )}
                    <NotificationItem
                      notification={notification}
                      onPress={() => handleNotificationPress(notification)}
                      onMarkAsRead={() => markAsRead(notification.id)}
                      colors={colors}
                    />
                  </View>
                ))}
              </View>
            </View>
          )}

          <View className="h-8" />
        </ScrollView>
      )}
      </SafeAreaView>
    </GestureHandlerRootView>
  );
}

